// THE HOST CONTRACT (contract 22 §5) — the shape 30/23/15/12/11 register INTO module 22.
// Every field belongs to the CONTRIBUTING feature: 22 stores, filters, sorts and renders an entry
// and never inspects a registrant's permission keys, route internals or copy (ledger §A11/§A14).

/** Settings groups in the order New Front renders them. */
export const SETTINGS_GROUP_KEYS = [
  'workspace',
  'account',
  'personal',
  'insights',
  'connections',
  'advanced',
] as const

export type SettingsGroupKey = (typeof SETTINGS_GROUP_KEYS)[number]

/**
 * A pure, synchronously-evaluable visibility predicate. The CONTRIBUTING feature owns it — 22 only
 * calls it, inside a try/catch (§14): a registrant whose RBAC has not hydrated yet hides its own
 * entry and never breaks the nav for the other four.
 */
export type SettingsVisibility = () => boolean

export interface SettingsNavEntry {
  /** Stable and unique across every registrant: 'workspace.basics' | 'workspace.definitions' | … */
  key: string
  /** i18n key IN THE OWNER'S NAMESPACE — never a literal (contract §13, non-negotiable #8). */
  labelKey: string
  /** Icon token (02 vocabulary). */
  icon: string
  group: SettingsGroupKey
  /** Ascending within the group. 22 reserves 0–99; registrants use 100+. */
  order: number
  /** Route name the entry navigates to — the registrant's OWN record. */
  routeName: string
  params?: Record<string, string>
  visible: SettingsVisibility
  /** Sub-entry parent key, e.g. `parent: 'workspace.definitions'`. */
  parent?: string
  /** Optional count/dot (e.g. pending invites). Evaluated defensively, exactly like `visible`. */
  badge?: () => string | number | null
}

export interface SettingsNavGroup {
  key: SettingsGroupKey
  labelKey: string
  entries: SettingsNavEntry[]
}

/** 22 owns the GROUP copy in its own namespace; each entry's label stays the registrant's. */
export function settingsGroupLabelKey(group: SettingsGroupKey): string {
  return `workspaceSettings.nav.group.${group}`
}
